import {
  faCalculator,
  faHourglassStart,
  faPlay,
  faRefresh,
} from '@fortawesome/pro-regular-svg-icons';
import { chain, orderBy } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import KeywordTable from '../../components/muiLegacy/Tables/KeywordTable/KeywordTable';
import {
  KeywordTableColumn,
  KeywordTableColumnTypes,
} from '../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/models';
import { TableUIState } from '../../components/muiLegacy/Tables/KeywordTable/logic/TableUIState';
import { useGetDebouncedKeywords } from '../../components/muiLegacy/Tables/KeywordTable/logic/useGetDebouncedKeywords';
import {
  KeywordTableState,
  useKeywordTableState,
} from '../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelAlert from '../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { DomainTypeGq } from '../../graphql/projects/models/DomainTypeGq';
import { GoogleAdsRequestGq } from '../../graphql/projects/models/GoogleAdsRequestGq';
import { KeywordFilteringGq } from '../../graphql/projects/models/KeywordFilteringGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { QueuedItemStatus } from '../../graphql/projects/models/QueuedItemStatus';
import { SerpStatusGq } from '../../graphql/projects/models/SerpStatusGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import { FrontendUserEventGq } from '../../graphql/users/queries/useFrontendLogUserEventGq';
import { usersApiService } from '../../graphql/users/usersApiService';
import { useDebounceValue } from '../../logic/otherUtilities/useDebounceValue';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import ProjectAppBar from '../ProjectPage/ProjectAppBar/ProjectAppBar';
import { useProjectPageTitle } from '../ProjectPage/useProjectPageTitle';
import MissingRankDomainPage from './MissingRankDomainPage';
import NextCheckInfo from './NextCheckInfo';
import ProgressDashboard from './ProgressDashboard/ProgressDashboard';
import { useApplyProgressDateSelect } from './ProgressDashboard/useApplyProgressDateSelect';
import { useProgressDateSelect } from './ProgressDashboard/useProgressDateSelect';
import RankTrackingSwitch from './RankTrackingSwitch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataTableToolbarButton from '../../components/voxel/DataTableToolbar/DataTableToolbarButton';
import { CircularProgress } from '@material-ui/core';
import LoadingItemSpinner from '../ProjectPage/ProjectAppBar/StatusIndicator/LoadingItem/LoadingItemSpinner';
import deepEqual from 'deep-equal';
import { useCalculateNow } from './useCalculateNow';

export interface ProgressPageProps {
  projectId: number;
  project: ProjectGq;
  user: CurrentUserGq;
  reload: () => Promise<unknown>;
}

const ProgressPage: React.FC<ProgressPageProps> = ({
  projectId,
  project: rootProject,
  user,
  reload,
}) => {
  // Log user event
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  useEffect(() => {
    logUserEventMutation({
      event: FrontendUserEventGq.VISIT_PROJECT_PROGRESS_PAGE,
      projectId: projectId,
    });
  }, [projectId]);

  // Project
  const { result: fetchedProject, query: projectQuery } = projectsApiService.queries.useGetProject({
    id: projectId,
  });

  const project = fetchedProject ?? rootProject;
  useProjectPageTitle('Progress', project);

  const requestsQuery = projectsApiService.queries.googleAds.useGetGoogleAdsRequests({
    projectId,
  });

  // Date selection
  const dateSelectBag = useProgressDateSelect(project);

  // Keyword fetch is loading
  const googleAdsRequestAnalysis = requestsQuery?.result?.analyzedRequests;
  const apiCollections = useMemo(() => {
    const result: Record<number, GoogleAdsRequestGq> = {};
    googleAdsRequestAnalysis.forEach((acc) => (result[acc.request.id] = acc.request));
    return result;
  }, [googleAdsRequestAnalysis]);

  // Download menu
  const [downloadOpen, setDownloadOpen] = useState(false);
  const onDownload = useCallback(() => setDownloadOpen(true), []);

  // Stored UI State
  const uiStateKey = `progress-ui-${projectId}`;
  const uiState = useMemo(() => {
    const storedState = sessionStorage.getItem(uiStateKey);
    if (!storedState?.length) return;
    try {
      return JSON.parse(storedState) as TableUIState;
    } catch {
      return undefined;
    }
  }, []);

  // Stored column State
  const columnStateKey = `progress-column-${projectId}`;
  const storedColumnState = useMemo(() => {
    const storedState = sessionStorage.getItem(columnStateKey);
    if (!storedState?.length) return;
    try {
      return JSON.parse(storedState) as KeywordTableColumn[];
    } catch {
      return undefined;
    }
  }, []);

  // Initial table state
  const initialColumnState: KeywordTableColumn[] = [
    { type: KeywordTableColumnTypes.Keyword, order: 0 },
    { type: KeywordTableColumnTypes.Volume, order: 1 },
    {
      type: KeywordTableColumnTypes.VolumeTrend,
      order: project.longMonthDisabled ? 3 : undefined,
    },
    ...project?.domains
      .filter((domain) => domain.type === DomainTypeGq.PROJECT)
      .map((domain, index) => ({
        type: KeywordTableColumnTypes.Rank,
        id: domain.id,
        order: index + 10,
        url: true,
        traffic: true,
      })),
  ];
  // Final column state
  let columnState = initialColumnState;
  if (storedColumnState?.length) columnState = storedColumnState;

  // Set initial filter state
  const initialFilterState = useMemo(() => {
    // Base filter
    const filter: Partial<KeywordFilteringGq> & { projectId: number } = {
      archived: false,
      projectId: projectId,
      serpStatusFilter: [SerpStatusGq.PROCESSING, SerpStatusGq.COMPLETED],
      isProgressPage: true,
    };
    return filter;
  }, [project?.projectSerpTrackingRequests]);

  // Setup keyword table state
  const keywordTableState = useKeywordTableState(
    columnState,
    initialFilterState,
    { invertSelection: false, keywordIds: [], emptyIsAll: false },
    {
      ...uiState, // ... Stored state ...
      enableColor: !!project.domains?.length,
    },
    project?.domains,
    project?.useAutomaticDomains,
    project?.tagGroups,
    { page: 0, amountPerPage: 100 },
    project.translated,
    project.hasEnglishTranslation,
    dateSelectBag.resetDates
  );

  // Update keyword filter based on dates
  useApplyProgressDateSelect(keywordTableState, dateSelectBag, project);

  // Set color as enabled if the number of domains have changed
  useEffect(
    () =>
      keywordTableState.setTableUIState((prev) => ({
        ...prev,
        enableColor: !!project.domains?.length,
      })),
    [!!project.domains?.length]
  );

  // Update stored state
  const debouncedUiState = useDebounceValue(keywordTableState.tableUIState, 500);

  useEffect(() => {
    let modifiedState = { ...debouncedUiState };
    const stringifiedState = JSON.stringify(modifiedState);
    sessionStorage.setItem(uiStateKey, stringifiedState);
  }, [debouncedUiState]);

  const debouncedColumnState = useDebounceValue(keywordTableState.columnPickerBag.columnState, 500);

  // Calculate now
  const {
    finalKeywordTableState,
    notCalculated,
    handleCalculateNow,
    setDashboardLoading,
    dashboardLoading,
  } = useCalculateNow(keywordTableState);

  // Column state
  useEffect(() => {
    let modifiedState = [...debouncedColumnState];
    const stringifiedState = JSON.stringify(modifiedState);
    sessionStorage.setItem(columnStateKey, stringifiedState);
  }, [debouncedColumnState]);

  // Keyword data
  const skip = !keywordTableState.filterState?.progressRequestIds?.length || !projectId;
  const domainIds = useMemo(
    () =>
      keywordTableState.columnPickerBag.columnState
        .filter((c) => c.type === KeywordTableColumnTypes.Rank && !!c.order && c.id)
        .map((c) => c.id),
    [keywordTableState.columnPickerBag.columnState]
  );
  const options = skip ? { skip: true } : {};
  const {
    keywords,
    totalCount,
    totalVolume,
    page,
    query: keywordQuery,
  } = useGetDebouncedKeywords(finalKeywordTableState, options, undefined, domainIds);
  const selectedKeywordStats = projectsApiService.queries.useGetSelectedKeywordsStats(
    {
      projectId: projectId,
      keywordQuery: {
        filtering: finalKeywordTableState.backendFilterState,
        selecting: { ...finalKeywordTableState.backendSelectionState, emptyIsAll: true },
      },
    },
    options
  );

  // Client domain
  const clientDomain = project.domains?.find((d) => d.type === DomainTypeGq.PROJECT);
  const noClientDomain = !clientDomain;

  const domainId = project?.domains?.find((t) => t.type === DomainTypeGq.PROJECT)?.id;

  const combinedReload = useCallback(() => {
    // This will block
    const blockingRefetch = [keywordQuery.refetch()];

    // These won't block
    projectQuery.refetch();
    reload();

    return Promise.all(blockingRefetch);
  }, [projectQuery.refetch, keywordQuery.refetch, reload]);

  const loaded = project;
  const isScrolling = useIsScrolling();

  // Ctr values
  const ctrQuery = projectsApiService.queries.useGetCtrValues({ dummy: true });
  const ctrQueryResult = !ctrQuery.query.loading ? ctrQuery?.result : [];
  const ctrValueDictionary = useMemo(
    () => Object.fromEntries(ctrQueryResult?.map((item) => [item.rank, item.ctr])),
    [ctrQueryResult?.length]
  );

  // Progress tracking - Completed tracking sessions
  const trackingRequests = orderBy(project?.projectSerpTrackingRequests, (pr) => pr.scheduledTime);
  const completedTrackingRequests = trackingRequests?.filter(
    (tr) => tr.status === QueuedItemStatus.SUCCEEDED
  );
  const lastProgressReqId = completedTrackingRequests?.slice(-1)[0]?.id;

  // Progress tracking - Next data retrieval
  const nextRequest = chain(trackingRequests)
    .filter((tr) => tr.status === QueuedItemStatus.QUEUED)
    .orderBy((tr) => tr.scheduledTime, 'asc')
    .first()
    .value();

  const nextDataRetrieval = nextRequest?.scheduledTime
    ? moment(nextRequest.scheduledTime).utc().format('yyyy-MM-DD')
    : 'TBD';

  const hasData = completedTrackingRequests.length >= 2;
  const isOn = !!project.serpTrackingOn;

  if (noClientDomain || !project.progressHasSerp) {
    return (
      <MissingRankDomainPage
        project={project}
        user={user}
        reload={reload}
        noDomain={noClientDomain}
        noRankData={!project.progressHasSerp}
      />
    );
  }

  return (
    <>
      <ProjectAppBar
        project={project}
        user={user}
        isScrolling={isScrolling}
        downloadOpen={downloadOpen}
      />

      {!!loaded && (
        <div
          className={
            'mt-14 w-full h-full mx-auto px-8 flex flex-row flex-nowrap items-center gap-12 relative'
          }
        >
          {/* Top switch */}
          {!hasData && (
            <div className="w-fit h-full flex flex-col items-stretch justify-stretch">
              <div className="h-full w-full flex flex-row gap-12 flex-nowrap items-center pt-14 pb-8">
                <RankTrackingSwitch
                  project={project}
                  reload={combinedReload}
                  disabled={!domainId || !project.progressHasSerp}
                />
                <div>
                  {!!project.serpTrackingOn && completedTrackingRequests.length >= 2 && (
                    <VoxelText variant="form-caption" align="left">
                      Next check: <NextCheckInfo project={project} />
                    </VoxelText>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* No data ready alert */}
          {isOn && !hasData && (
            <div className={' mx-auto my-3'}>
              <VoxelAlert
                title="Waiting for more data"
                severity="info"
                widthClass="max-w-[48rem]"
                size="sm"
                iconPosition="center"
                fontAIconName={faHourglassStart}
              >
                Rank tracking needs more data points. The next rank tracking data retrieval is
                planned on <span className="font-semibold">{nextDataRetrieval}</span>.
              </VoxelAlert>
            </div>
          )}
        </div>
      )}

      {hasData && (
        <div className="w-full h-full mx-auto px-8 mb-4">
          <ProgressDashboard
            filter={finalKeywordTableState.backendFilterState}
            project={project}
            user={user}
            reloadKeywords={combinedReload}
            keywordTableState={keywordTableState}
            dateSelectBag={dateSelectBag}
            setDashboardLoading={setDashboardLoading}
          />
        </div>
      )}

      {/* Keyword UI */}
      {hasData && (
        <div className="w-full h-full mx-auto px-8 pb-1">
          <KeywordTable
            requests={apiCollections}
            extraButtons={
              <DataTableToolbarButton
                label={
                  keywordQuery.loading || dashboardLoading
                    ? 'Loading'
                    : notCalculated
                    ? 'Apply filter'
                    : 'Refresh'
                }
                className={notCalculated ? undefined : 'w-[88px] gap-1'}
                effect={notCalculated ? 'primary' : undefined}
                toggle={notCalculated}
                onClick={notCalculated ? handleCalculateNow : combinedReload}
                loading
                disabled={keywordQuery.loading || dashboardLoading}
                effectAnimate={notCalculated}
                icon={
                  keywordQuery.loading || dashboardLoading ? (
                    <LoadingItemSpinner sizeClass="h-2.5 w-2.5" />
                  ) : (
                    <FontAwesomeIcon icon={notCalculated ? faPlay : faRefresh} />
                  )
                }
                tooltip={
                  notCalculated
                    ? `The filter has changed. Click button to refresh keyword table and dashboard`
                    : `Refresh keyword table`
                }
              />
            }
            project={project}
            selectedKeywordStats={selectedKeywordStats.result}
            totalCount={totalCount}
            totalVolume={totalVolume}
            currentPage={page}
            keywordTableState={keywordTableState}
            includeExportMonths={true}
            reloadKeywords={combinedReload}
            data={keywords}
            tagGroups={project.tagGroups}
            onDownload={onDownload}
            changeVisibleColumns={true}
            isScrolling={isScrolling}
            rankCtrValues={ctrValueDictionary}
            page="progress"
            lastProgressReqId={lastProgressReqId}
          />
        </div>
      )}
    </>
  );
};

export default ProgressPage;
